import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type Ref,
  type PointerEvent,
  type MouseEvent,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { curves, durations } from '../theme/motion';
import { haptic } from './haptic';
import { mergeRefs } from './mergeRefs';
import { Portal } from './Portal';
import styles from './Tooltip.module.css';

// Material Tooltip: hover on desktop, long-press on touch (shown 1.5 s after
// release), placed 24 px from the target centre, below when it fits.

const VERTICAL_OFFSET = 24;
const SCREEN_MARGIN = 8;
const LONG_PRESS_MS = 500;
const TOUCH_SLOP = 18;
const HOVER_EXIT_MS = 100;
/** Mobile browsers often send no click after a long press; forget the swallow by then. */
const SUPPRESS_CLICK_MS = 400;

type TriggerProps = {
  ref?: Ref<HTMLElement>;
  onPointerEnter?: (e: PointerEvent<HTMLElement>) => void;
  onPointerLeave?: (e: PointerEvent<HTMLElement>) => void;
  onPointerDown?: (e: PointerEvent<HTMLElement>) => void;
  onPointerMove?: (e: PointerEvent<HTMLElement>) => void;
  onPointerUp?: (e: PointerEvent<HTMLElement>) => void;
  onPointerCancel?: (e: PointerEvent<HTMLElement>) => void;
  onClickCapture?: (e: MouseEvent<HTMLElement>) => void;
  onContextMenu?: (e: MouseEvent<HTMLElement>) => void;
  'aria-describedby'?: string;
};

export interface TooltipProps {
  message: ReactNode;
  /** A single element that accepts pointer handlers and a ref. */
  children: ReactElement<TriggerProps>;
  /** Hover delay (Flutter default 0 ms). */
  waitMs?: number;
  /** How long a long-press tooltip stays after release (Flutter default 1.5 s). */
  showMs?: number;
  /** Only show when the trigger's text is visibly truncated. */
  onlyWhenTruncated?: boolean;
  disabled?: boolean;
}

interface Placement {
  left: number;
  top: number;
  below: boolean;
}

function isTruncated(element: HTMLElement): boolean {
  if (element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > element.clientHeight + 1) return true;
  return Array.from(element.querySelectorAll<HTMLElement>('*')).some((child) => child.scrollWidth > child.clientWidth + 1);
}

export function Tooltip({ message, children, waitMs = durations.tooltipWait, showMs = durations.tooltipShow, onlyWhenTruncated, disabled }: TooltipProps) {
  const id = useId();
  // Reduced motion: the bubble appears and disappears without a fade.
  const fade = (useReducedMotion() ?? false) ? 0 : 1;
  const triggerRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const timers = useRef<{
    show?: ReturnType<typeof setTimeout>;
    hide?: ReturnType<typeof setTimeout>;
    suppress?: ReturnType<typeof setTimeout>;
  }>({});
  const press = useRef<{ x: number; y: number; fired: boolean } | null>(null);
  const suppressClick = useRef(false);

  const clearTimers = () => {
    clearTimeout(timers.current.show);
    clearTimeout(timers.current.hide);
  };
  const setSuppressClick = (value: boolean) => {
    clearTimeout(timers.current.suppress);
    suppressClick.current = value;
  };
  const show = useCallback(() => {
    const el = triggerRef.current;
    if (disabled || !el) return;
    if (onlyWhenTruncated && !isTruncated(el)) return;
    setPlacement(null);
    setVisible(true);
  }, [disabled, onlyWhenTruncated]);
  const hide = useCallback(() => setVisible(false), []);

  useEffect(
    () => () => {
      clearTimers();
      clearTimeout(timers.current.suppress);
    },
    [],
  );
  useEffect(() => {
    if (!visible) return;
    const dismiss = () => setVisible(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    // The trigger can leave without a pointerleave (its layer closes or turns
    // inert under a still pointer, e.g. a sheet animating out after Escape).
    const watch = setInterval(() => {
      const el = triggerRef.current;
      if (!el || !el.isConnected || el.closest('[inert]')) dismiss();
    }, 150);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      clearInterval(watch);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [visible]);

  useLayoutEffect(() => {
    if (!visible || placement) return;
    const el = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!el || !bubble) return;
    const rect = el.getBoundingClientRect();
    const { width, height } = bubble.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;
    const below = centerY + VERTICAL_OFFSET + height <= window.innerHeight - SCREEN_MARGIN;
    const top = below ? centerY + VERTICAL_OFFSET : Math.max(SCREEN_MARGIN, centerY - VERTICAL_OFFSET - height);
    const left = Math.min(
      Math.max(SCREEN_MARGIN, rect.left + rect.width / 2 - width / 2),
      Math.max(SCREEN_MARGIN, window.innerWidth - width - SCREEN_MARGIN),
    );
    setPlacement({ left, top, below });
  }, [visible, placement]);

  const child = children;
  const props = child.props;
  const triggerProps: TriggerProps = {
    ref: mergeRefs(triggerRef, props.ref),
    onPointerEnter: (e) => {
      props.onPointerEnter?.(e);
      if (e.pointerType !== 'mouse') return;
      clearTimers();
      timers.current.show = setTimeout(show, waitMs);
    },
    onPointerLeave: (e) => {
      props.onPointerLeave?.(e);
      if (e.pointerType !== 'mouse') return;
      clearTimers();
      timers.current.hide = setTimeout(hide, HOVER_EXIT_MS);
    },
    onPointerDown: (e) => {
      props.onPointerDown?.(e);
      setSuppressClick(false);
      if (e.pointerType === 'mouse') {
        clearTimers();
        hide();
        return;
      }
      press.current = { x: e.clientX, y: e.clientY, fired: false };
      clearTimers();
      timers.current.show = setTimeout(() => {
        if (!press.current) return;
        press.current.fired = true;
        setSuppressClick(true);
        haptic('light');
        show();
      }, LONG_PRESS_MS);
    },
    onPointerMove: (e) => {
      props.onPointerMove?.(e);
      const p = press.current;
      if (p && !p.fired && Math.hypot(e.clientX - p.x, e.clientY - p.y) > TOUCH_SLOP) {
        press.current = null;
        clearTimeout(timers.current.show);
      }
    },
    onPointerUp: (e) => {
      props.onPointerUp?.(e);
      const p = press.current;
      press.current = null;
      if (p?.fired) {
        timers.current.hide = setTimeout(hide, showMs);
        timers.current.suppress = setTimeout(() => setSuppressClick(false), SUPPRESS_CLICK_MS);
      } else if (e.pointerType !== 'mouse') clearTimeout(timers.current.show);
    },
    onPointerCancel: (e) => {
      props.onPointerCancel?.(e);
      const p = press.current;
      press.current = null;
      clearTimeout(timers.current.show);
      if (p?.fired) timers.current.hide = setTimeout(hide, showMs);
      setSuppressClick(false);
    },
    onClickCapture: (e) => {
      if (suppressClick.current) {
        setSuppressClick(false);
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      props.onClickCapture?.(e);
    },
    onContextMenu: (e) => {
      if (press.current) e.preventDefault();
      props.onContextMenu?.(e);
    },
    'aria-describedby': visible ? id : props['aria-describedby'],
  };

  return (
    <>
      {cloneElement(child, triggerProps)}
      <Portal>
        <AnimatePresence>
          {visible && (
            <motion.div
              ref={bubbleRef}
              id={id}
              role="tooltip"
              className={styles.tooltip}
              style={placement ? { left: placement.left, top: placement.top } : { left: -9999, top: -9999 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: placement ? 1 : 0, transition: { duration: (fade * durations.tooltipFadeIn) / 1000, ease: curves.easeOut } }}
              exit={{ opacity: 0, transition: { duration: (fade * durations.tooltipFadeOut) / 1000, ease: curves.easeOut } }}
            >
              {message}
            </motion.div>
          )}
        </AnimatePresence>
      </Portal>
    </>
  );
}
